import type { FC } from 'react';
import { useCallback } from 'react';
import type { Control } from 'react-hook-form';

import type { TeamingReason } from 'features/teaming/teaming-base/enums';
import { useAppDispatch } from 'features/app/hooks';
import { handleError } from 'features/notification/store';
import { TeamingBaseRequestForm } from 'features/teaming/teaming-base/components';
import type { TeamRequestFormOptions } from 'features/teaming/teaming-base/interfaces';
import {
  requestInPersonTeaming,
  setTeamingReason,
} from 'features/teaming/teaming-base/store';
import type { CustomSelectItem } from 'features/ui/elements';

interface TeamingInPersonRequestFormProps {
  control: Control<TeamRequestFormOptions>;
  isValid: boolean;
  isLoading: boolean;
  getFormValues: () => TeamRequestFormOptions;
  setLoading: (loading: boolean) => void;
  reasonOptions: CustomSelectItem[];
  locationOptions: CustomSelectItem[];
  closePopover: () => void;
}

export const TeamingInPersonRequestForm: FC<
  TeamingInPersonRequestFormProps
> = ({
  control,
  isValid,
  isLoading,
  getFormValues,
  setLoading,
  reasonOptions,
  closePopover,
  locationOptions,
}) => {
  const dispatch = useAppDispatch();

  const handleRequestTeaming = useCallback(
    async () => {
      setLoading(true);
      try {
        const formOptions = getFormValues();
        dispatch(setTeamingReason(formOptions.reason as TeamingReason));
        await dispatch(requestInPersonTeaming(formOptions)).unwrap();
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'handleRequestTeaming',
          })
        );
      } finally {
        setLoading(false);
        closePopover();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePopover]
  );

  return (
    <TeamingBaseRequestForm
      onSubmitButtonClick={handleRequestTeaming}
      control={control}
      isLoading={isLoading}
      isValid={isValid}
      reasonOptions={reasonOptions}
      locationOptions={locationOptions}
      closePopover={closePopover}
    />
  );
};
